import React, { useEffect, useRef, useState } from "react";
import { useIsFocused, useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";
import { _goToAddFactor } from "routers/helper";

import {
  View,
  FlatList,
  SafeAreaView,
  TouchableOpacity,
  Platform,
} from "react-native";
import {
  Text,
  Icon,
  Input,
  Error,
  Empty,
  Header,
  Loading,
  FactorBox,
  AddButtonFloat,
  AnimatedInfinityList,
} from "components";

import { useFactors, useFactorStatuses } from "clients/apis";

import { colors, sizes, strings } from "assets/constants";
import { mq, liveSearchInput } from "utils";
import { useChmod, useFlag } from "hooks";
import css from "styles";
import { useRecoilValue } from "recoil";
import { authAtom, factorStepsAtom } from "stores";
import TodayFactorsExport from "./TodayFactorsExport";

const viewOffset = sizes.screenW / 2 - 40;
const categoryItemHeight = mq(42);
const ListSeparator = () => (
  <View
    style={{
      width: mq(3),
      height: categoryItemHeight,
      backgroundColor: colors.screensBg,
    }}
  />
);

let timer: ReturnType<typeof setTimeout>;

export default function Factors() {
  const { chmod, factorStepsAllowed } = useChmod();
  const tabs = useRef<FlatList>(null);

  const {
    company: { color },
  } = useRecoilValue(authAtom);
  const isFocused = useIsFocused();
  const { archive } = useRoute<RootRouteProps<"factors">>().params;
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();

  const {
    isLoading: factorStatusLoading,
    data: factorStatuses,
    refetch: refreshFactorStatuses,
  } = useFactorStatuses();

  const allowedFactorStatus = [
    ...(factorStatuses?.data?.statuses
      ? Object.values(factorStatuses?.data?.statuses as FactorStatusT).filter(
          (e) => factorStepsAllowed().includes(e.id)
        )
      : []),
  ];

  const [selectedStatus, setSelectedStatus] = useState<FactorStatusT>({
    id: factorStepsAllowed()?.[0],
  });

  const [name, setName] = useState("");
  const {
    data,
    error,
    isError,
    refetch,
    isLoading,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFactors({
    name,
    archive,
    status: selectedStatus.id,
  });

  useEffect(() => {
    if (isFocused) {
      refetch();
      refreshFactorStatuses();
    }
  }, [isFocused, refetch, refreshFactorStatuses]);

  const _renderStatus = (params: { item: FactorStatusT; index: number }) => {
    const { item, index } = params;

    const currentIndex =
      Platform.OS === "web" ? allowedFactorStatus.length - index - 1 : index;

    const active = selectedStatus.id === item.id;

    const _onSelect = () => {
      tabs.current?.scrollToIndex({
        index: currentIndex,
        animated: true,
        viewOffset,
      });
      setSelectedStatus(item);
    };

    const contentColor = active ? color : colors.text.gray;

    return (
      <TouchableOpacity
        onPress={_onSelect}
        activeOpacity={sizes.tapOpacity}
        style={{
          ...css.centralElements,
          paddingHorizontal: mq(20),
          height: categoryItemHeight,
          backgroundColor: active ? colors.white : colors.innerItemsBg,
        }}
      >
        <Text
          style={{ top: mq(4) }}
          size={"sm"}
          weight="bold"
          color={active ? color : colors.textColorGray}
        >
          {item.title}
        </Text>

        <View style={{ ...css.rRowCentral, height: mq(25), bottom: mq(4) }}>
          <Icon color={contentColor} type="oct" name={"checklist"} size={11} />
          {Number(item.factors_count) > 0 ? (
            <View style={{ marginRight: mq(5) }}>
              <Text color={contentColor} weight="extraBold" size={"sm"}>
                {item.factors_count}
              </Text>
            </View>
          ) : (
            <View
              style={{
                ...css.absolute,
                width: 1,
                height: 15,
                top: 7,
                backgroundColor: contentColor,
                transform: [{ rotate: "60deg" }],
              }}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const _renderItem = (items: []) => {
    return (
      <View style={{ ...css.wrap, ...css.rRowCenterAlignSpaceBetween }}>
        {items.map((factor: FactorT, key: number) => (
          <View key={String(key)} style={css.responsiveBoxContainer}>
            <FactorBox
              refreshList={() => {
                refreshFactorStatuses();
                refetch();
              }}
              actions={["delete"]}
              statuses={factorStatuses?.data?.statuses}
              factor={factor}
              activeStep={selectedStatus}
            />
          </View>
        ))}
      </View>
    );
  };

  const _renderContent = () => {
    if (isLoading || typing || factorStatusLoading) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    if (data?.pages?.[0]?.data?.data?.length === 0) {
      return (
        <Empty style={{ marginTop: mq(20) }} message={strings.no_factor} />
      );
    }

    return (
      <AnimatedInfinityList
        responseData={data}
        onRefresh={refetch}
        renderItemList={_renderItem}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        paddingTop={sizes.space}
        paddingBottom={100}
      />
    );
  };

  return (
    <>
      <Header title={archive ? strings.factors_archive : strings.factors} />
      <>
        <SafeAreaView style={{ ...css.container, ...css.screenBg }}>
          <View
            style={{
              ...css.paddingH,
              ...css.rowCenterAlign,
              marginTop: sizes.space,
              marginBottom: sizes.space * 0.3,
            }}
          >
            <View
              style={{
                ...css.container,
                ...css.colCenterAlignY,
              }}
            >
              <Input
                onChangeText={(text) =>
                  liveSearchInput(
                    text,
                    name,
                    setName,
                    timer,
                    startTyping,
                    stopTyping
                  )
                }
                placeholder={strings.insert_name_search}
                icon={{ name: "search" }}
                hideError
              />
            </View>
          </View>
          {factorStatuses?.data?.statuses && (
            <View
              style={{
                height: categoryItemHeight,
                borderBottomWidth: 1,
                borderColor: colors.border + "55",
                backgroundColor: colors.white,
                marginTop: sizes.space,
              }}
            >
              <FlatList
                keyExtractor={(_, key) => String(key)}
                ref={tabs}
                inverted
                horizontal
                showsHorizontalScrollIndicator={false}
                ItemSeparatorComponent={ListSeparator}
                contentContainerStyle={{
                  ...css.paddingH,
                  ...css.growContainer,
                  ...css.centralElements,
                  marginTop: mq(6),
                  marginBottom: mq(3),
                }}
                data={allowedFactorStatus}
                renderItem={_renderStatus}
              />
            </View>
          )}

          {_renderContent()}
        </SafeAreaView>
      </>
      {!archive && (
        <>
          {chmod("factor", "POST") && (
            <AddButtonFloat onPress={_goToAddFactor} />
          )}
          {data && <TodayFactorsExport />}
        </>
      )}
    </>
  );
}
